/**
 * Step 4 of the import wizard (plan task E6): the actual write. The parent
 * (`ImportMembersPage`) owns the `POST /members/import/commit` call and its
 * outcome; this component is a pure view over that state machine so the
 * long-running request (bcrypt-hashing every new account's temporary
 * password — 20 to 40+ seconds for a gym's full member list, per the E3
 * contract) has exactly one place that decides what to show:
 *
 * - `ready`: a recap of the preview's counts and the one "point of no
 *   return" action.
 * - `committing`: the "no cierres esta ventana" state — no button is
 *   rendered at all (not just disabled), so there is nothing left to
 *   double-click while the request is in flight.
 * - `done`: the result workbook already downloaded automatically; the
 *   passwords-are-shown-once warning and a manual re-download for when the
 *   browser blocked the automatic one.
 * - `error`: what to offer next depends on `errorKind`, which the parent
 *   derives from the failure (see `classifyError` in ImportMembersPage) —
 *   an expired upload can only be restarted, an already-committed import
 *   with no result left can only be looked up in the members list, and an
 *   uncertain outcome (a network error while the request was in flight, or
 *   a retry that finds the previous attempt DID go through) offers
 *   re-downloading that previous result before ever considering a retry
 *   that could double-run the import.
 */
import { TbAlertTriangle } from 'react-icons/tb';

export function ConfirmStep({
  summary, updateExisting, status, error, errorKind, result,
  onConfirm, onBack, onDownloadAgain, onDownloadResult, onRestart, onGoToMembers,
}) {
  if (status === 'committing') {
    return (
      <div className="gym-card gym-import-confirm">
        <p role="status" aria-live="polite" className="gym-import-status gym-import-confirm__committing">
          <span className="gym-state__spinner" aria-hidden="true" />
          Importando socios… no cierres esta ventana.
        </p>
      </div>
    );
  }

  if (status === 'done' && result) {
    const { summary: committed } = result;
    return (
      <div className="gym-card gym-import-confirm">
        <p className="gym-import-confirm__done-lead">Importación completada.</p>
        <ul className="gym-import-confirm__done-counts">
          <li>Creados: {committed.created}</li>
          <li>Actualizados: {committed.updated}</li>
          <li>Sin cambios: {committed.unchanged}</li>
          <li>Con errores: {committed.errors}</li>
          <li>Períodos creados: {committed.memberships}</li>
          <li>Pagos registrados: {committed.payments}</li>
        </ul>
        <p className="gym-import-confirm__filename">{result.filename}</p>
        <p role="alert" className="gym-import-confirm__warning">
          Guardá el archivo descargado: las contraseñas temporales no se vuelven a mostrar.
        </p>
        <div className="gym-admin-form-actions">
          <button type="button" className="gym-button gym-button--primary" onClick={onDownloadAgain}>Descargar de nuevo</button>
          <button type="button" className="gym-button gym-button--secondary" onClick={onGoToMembers}>Ir a Socios</button>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="gym-card gym-import-confirm">
        <p role="alert" className="gym-form__error">{error}</p>
        <div className="gym-admin-form-actions">
          {errorKind === 'expired' && (
            <button type="button" className="gym-button gym-button--primary" onClick={onRestart}>Volver a empezar</button>
          )}
          {errorKind === 'already_committed' && (
            <button type="button" className="gym-button gym-button--primary" onClick={onGoToMembers}>Ir a Socios</button>
          )}
          {(errorKind === 'already_committed_result' || errorKind === 'network_uncertain') && (
            <>
              <button type="button" className="gym-button gym-button--primary" onClick={onDownloadResult}>Descargar resultado</button>
              <button type="button" className="gym-button gym-button--secondary" onClick={onConfirm}>Reintentar</button>
            </>
          )}
          {errorKind === 'other' && (
            <>
              <button type="button" className="gym-button gym-button--primary" onClick={onConfirm}>Reintentar</button>
              <button type="button" className="gym-button gym-button--secondary" onClick={onBack}>Volver</button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="gym-card gym-import-confirm">
      <p>Se van a crear <strong>{summary.toCreate}</strong> socios y actualizar <strong>{summary.toUpdate}</strong>
        {updateExisting ? '' : ' (activá "Actualizar socios existentes" en el paso anterior para actualizar también)'}.
        {summary.errors > 0 && ` ${summary.errors} filas con error se van a omitir.`}
      </p>
      <p className="gym-import-confirm__warning">
        <span className="gym-import-confirm__warning-icon" aria-hidden="true"><TbAlertTriangle /></span>
        Esta acción no se puede deshacer y puede tardar más de un minuto. No cierres esta ventana mientras se procesa.
      </p>
      <div className="gym-admin-form-actions">
        <button type="button" className="gym-button gym-button--primary" onClick={onConfirm}>Confirmar e importar</button>
        <button type="button" className="gym-button gym-button--secondary" onClick={onBack}>Volver</button>
      </div>
    </div>
  );
}
